/**
 * Unified Template and Layout View
 * Split layout: 30% sidebar with template thumbnails + 70% detail panel with large preview
 */

import { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { styleAPI } from '../../services/api';
import { Loader2, Check, Package, Layout } from 'lucide-react';
import { applyTemplate } from '@/services/workflowActions';
import { useWorkflowStore } from '@/stores/workflowStore';
import { ImageCarousel } from './ImageCarousel';
import { cn } from '@/lib/utils';

interface Style {
  id: string;
  name: string;
  description: string;
  isDefault: boolean;
  layouts: string[];
  previewImage?: string;
  images?: string[];
}

interface TemplateAndLayoutViewProps {
  conversationId?: string | null;
}

export function TemplateAndLayoutView({ conversationId }: TemplateAndLayoutViewProps) {
  const [templates, setTemplates] = useState<Style[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const workflowStore = useWorkflowStore();

  // Get applied template from workflow store
  const workflowState = conversationId ? workflowStore.getWorkflowState(conversationId) : null;
  const appliedTemplateId = workflowState?.steps.template_selection.selectedTemplate;

  // Fetch available templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await styleAPI.getAll();

        if (response.success && response.data) {
          setTemplates(response.data);

          // Set initial selection to applied template or first template
          if (appliedTemplateId) {
            setSelectedTemplateId(appliedTemplateId);
          } else if (response.data.length > 0) {
            setSelectedTemplateId(response.data[0].id);
          }
        } else {
          setError('Failed to load templates');
        }
      } catch (err) {
        console.error('Failed to fetch templates:', err);
        setError(err instanceof Error ? err.message : 'Failed to load templates');
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [appliedTemplateId]);

  const handleApplyTemplate = async () => {
    if (!conversationId || !selectedTemplateId) {
      alert('Please start a conversation first');
      return;
    }

    const selectedTemplate = templates.find(t => t.id === selectedTemplateId);
    if (!selectedTemplate) return;

    try {
      setApplyingTemplate(true);
      await applyTemplate(conversationId, selectedTemplate.id, selectedTemplate.name);
    } catch (error) {
      console.error('Failed to apply template:', error);
      alert(`Failed to apply template: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setApplyingTemplate(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Loading templates...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-destructive">Error: {error}</div>
      </div>
    );
  }

  if (!conversationId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground text-center">
          <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>Start a conversation to select a template</p>
        </div>
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground text-center">
          <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>No templates available</p>
          <p className="text-sm mt-2">Contact your administrator to add templates</p>
        </div>
      </div>
    );
  }

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);
  const isApplied = appliedTemplateId === selectedTemplateId;

  return (
    <div>
      {/* Split Layout: Sidebar + Detail Panel */}
      <div className="flex gap-4 h-[500px]">
        {/* Left Sidebar (30%) - Template List */}
        <div className="w-[30%] min-w-[200px] space-y-2 overflow-y-auto pr-2">
          {templates.map((template) => (
            <TemplateSidebarCard
              key={template.id}
              template={template}
              isSelected={selectedTemplateId === template.id}
              isApplied={appliedTemplateId === template.id}
              onClick={() => setSelectedTemplateId(template.id)}
            />
          ))}
        </div>

        {/* Right Panel (70%) - Detail View */}
        <div className="flex-1 w-[70%]">
          <Card className="relative overflow-hidden border-2 transition-all duration-300"
            style={{
              borderColor: isApplied ? 'rgb(34, 197, 94)' : undefined,
            }}
          >
            {/* Applied Badge */}
            {isApplied && (
              <div className="absolute top-3 right-3 bg-green-500 text-white text-xs font-medium px-3 py-1.5 rounded-full flex items-center gap-1.5 z-10 shadow-lg">
                <Check className="h-3.5 w-3.5" />
                Currently Applied
              </div>
            )}

            {/* Content Container */}
            <div className="flex flex-col">
              {/* Large Image Carousel */}
              <div className="relative aspect-video bg-gradient-to-br from-primary/10 to-primary/5 flex-shrink-0">
                {selectedTemplate && (
                  <ImageCarousel
                    previewImage={selectedTemplate.previewImage || selectedTemplate.images?.[0]}
                    images={selectedTemplate.images}
                    styleName={selectedTemplate.name}
                    styleId={selectedTemplate.id}
                    className="h-full"
                  />
                )}
              </div>

              {/* Template Info */}
              <div className="p-4 bg-card border-t border-border/30">
                {selectedTemplate && (
                  <div className="space-y-4">
                    {/* Template Name & Description */}
                    <div>
                      <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                        <Package className="h-5 w-5 text-primary" />
                        {selectedTemplate.name}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {selectedTemplate.description}
                      </p>
                    </div>

                    {/* Layout Count */}
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Layout className="h-4 w-4" />
                      <span>{selectedTemplate.layouts.length} layout{selectedTemplate.layouts.length !== 1 ? 's' : ''} available</span>
                    </div>

                    {/* Apply Button */}
                    <Button
                      onClick={handleApplyTemplate}
                      disabled={applyingTemplate || isApplied}
                      className="w-full"
                      size="default"
                      variant={isApplied ? 'outline' : 'default'}
                    >
                      {applyingTemplate ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Applying Template...
                        </>
                      ) : isApplied ? (
                        <>
                          <Check className="h-4 w-4 mr-2" />
                          Template Applied
                        </>
                      ) : (
                        <>
                          <Package className="h-4 w-4 mr-2" />
                          Apply Template
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

// Compact Template Card for Sidebar
interface TemplateSidebarCardProps {
  template: Style;
  isSelected: boolean;
  isApplied: boolean;
  onClick: () => void;
}

function TemplateSidebarCard({ template, isSelected, isApplied, onClick }: TemplateSidebarCardProps) {
  return (
    <Card
      className={cn(
        "relative overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-md group",
        isSelected ? "ring-2 ring-primary shadow-md" : "hover:border-primary/50",
        isApplied && "border-green-500/50"
      )}
      onClick={onClick}
    >
      {/* Preview Image */}
      <div className="relative aspect-video bg-gradient-to-br from-muted/30 to-muted/10 overflow-hidden">
        {(template.previewImage || template.images?.[0]) && (
          <img
            src={`/styles/${template.id}/${template.previewImage || template.images?.[0]}`}
            alt={template.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
        )}

        {/* Applied Badge */}
        {isApplied && (
          <div className="absolute top-1.5 right-1.5 bg-green-500 text-white text-[10px] font-medium px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
            <Check className="h-2.5 w-2.5" />
          </div>
        )}

        {/* Overlay on hover */}
        <div className={cn(
          "absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent transition-opacity",
          isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
        )} />
      </div>

      {/* Template Name */}
      <div className="p-2 bg-card/95 backdrop-blur-sm border-t border-border/30">
        <p className={cn(
          "text-xs font-medium truncate transition-colors",
          isSelected ? "text-primary" : "text-foreground"
        )}>
          {template.name}
        </p>
        <p className="text-[10px] text-muted-foreground truncate">
          {template.layouts.length} layout{template.layouts.length !== 1 ? 's' : ''}
        </p>
      </div>
    </Card>
  );
}
